import { DestroyRef, inject, Injectable, Signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ToastrService } from 'ngx-toastr';
import { catchError, finalize, forkJoin, Observable, of, switchMap, tap } from 'rxjs';
import {
  IApiReservation,
  IBookingRequest,
  IBulkDisponibilityRequest,
  ICreateDisponibilityRequest,
  IDispoResponse,
  IUpdateAppointmentRequest,
  IUpdateDisponibilityRequest,
  IUserReservation
} from '../../interfaces/reservation.interface';
import { AddressParserService } from './address-parser.service';
import { ReservationApiService } from './reservation-api.service';
import { ReservationNotificationService } from './reservation-notification.service';
import { ReservationStateService } from './reservation-state.service';
import { ReservationValidatorService } from './reservation-validator.service';

/**
 * Service orchestrateur pour la gestion des réservations
 * Délègue les responsabilités aux services spécialisés
 */
@Injectable({
  providedIn: 'root'
})
export class ReservationsService {
  #apiService: ReservationApiService = inject(ReservationApiService);
  #stateService: ReservationStateService = inject(ReservationStateService);
  #notificationService: ReservationNotificationService = inject(ReservationNotificationService);
  #validatorService: ReservationValidatorService = inject(ReservationValidatorService);
  #addressParser: AddressParserService = inject(AddressParserService);
  #toastrService: ToastrService = inject(ToastrService);
  #destroyRef: DestroyRef = inject(DestroyRef);

  // Exposer les signals du state service
  loading: Signal<boolean> = this.#stateService.loading;
  loadingDisponibilities: Signal<boolean> = this.#stateService.loadingDisponibilities;
  loadingReservations: Signal<boolean> = this.#stateService.loadingReservations;
  userReservation: Signal<IUserReservation[]> = this.#stateService.userReservation;
  hairdresserReservation: Signal<IApiReservation[]> = this.#stateService.hairdresserReservation;
  updatingReservations = this.#stateService.updatingReservations;
  isPolling = this.#stateService.isPolling;
  lastUpdateTime = this.#stateService.lastUpdateTime;

  getUserReservations(): Observable<IUserReservation[]> {
    this.#stateService.getUserReservations();
    // Retourner directement depuis l'API pour maintenir la compatibilité Observable
    return this.#apiService.getUserReservations().pipe(
      tap((res: IUserReservation[]) => {
        // L'état est déjà mis à jour par getUserReservations()
      })
    );
  }

  createReservation(bookingData: IBookingRequest): Observable<IUserReservation> {
    return this.#apiService.createReservation(bookingData).pipe(
      tap((reservation: IUserReservation) => {
        const currentReservations = this.#stateService.userReservation();
        // Note: L'état sera mis à jour par le state service si nécessaire
        this.#toastrService.success('Reservation created successfully!', 'Success');
      }),
      catchError((error) => {
        throw error;
      })
    );
  }

  getHairdresserReservations(forceRefresh: boolean = false): Observable<IApiReservation[]> {
    this.#stateService.getHairdresserReservations(forceRefresh);
    // Retourner directement depuis l'API pour maintenir la compatibilité Observable
    return this.#apiService.getHairdresserReservations().pipe(
      tap((res: IApiReservation[]) => {
        // L'état est déjà mis à jour par getHairdresserReservations()
      })
    );
  }

  startAutoUpdate(): void {
    this.#stateService.startAutoUpdate();
  }

  stopAutoUpdate(): void {
    this.#stateService.stopAutoUpdate();
  }

  forceUpdate(): void {
    this.#stateService.forceUpdate();
  }

  async requestNotificationPermission(): Promise<boolean> {
    return this.#notificationService.requestNotificationPermission();
  }

  getDisponibilities(hairdresserId: number): Observable<IDispoResponse[]> {
    // Appeler seulement l'API une fois et retourner l'observable
    return this.#apiService.getDisponibilities(hairdresserId).pipe(
      tap((res: IDispoResponse[]) => {
        // Mettre à jour l'état après avoir reçu les données
        this.#stateService.updateDisponibilitiesLocally(res);
      })
    );
  }

  postDisponibility(disponibility: ICreateDisponibilityRequest): Observable<any> {
    return this.#apiService.postDisponibility(disponibility).pipe(
      tap(() => {
        this.#toastrService.success('Disponibilité ajoutée avec succès!', 'Succès');
      }),
      catchError(err => {
        console.error('Error creating disponibility:', err);
        this.#toastrService.error('Erreur lors de la création de la disponibilité', 'Erreur');
        throw err;
      })
    );
  }

  addBulkDisponibilities(disponibilities: IBulkDisponibilityRequest): Observable<any> {
    return this.#apiService.addBulkDisponibilities(disponibilities).pipe(
      tap(() => {
        this.#toastrService.success('Disponibilités ajoutées avec succès!', 'Succès');
      }),
      catchError(err => {
        console.error('Error adding bulk disponibilities:', err);
        this.#toastrService.error('Erreur lors de l\'ajout des disponibilités', 'Erreur');
        throw err;
      })
    );
  }

  updateDisponibility(disponibility: IUpdateDisponibilityRequest): Observable<any> {
    return this.#apiService.updateDisponibility(disponibility).pipe(
      tap(() => {
        this.#toastrService.success('Disponibilité modifiée avec succès!', 'Succès');
      }),
      catchError(err => {
        console.error('Error updating disponibility:', err);
        this.#toastrService.error('Erreur lors de la modification de la disponibilité', 'Erreur');
        throw err;
      })
    );
  }

  deleteDisponibility(id: number): Observable<any> {
    return this.#apiService.deleteDisponibility(id).pipe(
      tap(() => {
        this.#toastrService.success('Disponibilité supprimée avec succès!', 'Succès');
        this.#stateService.removeDisponibilityLocally(id);
      }),
      catchError(err => {
        console.error('Error deleting disponibility:', err);
        this.#toastrService.error('Erreur lors de la suppression de la disponibilité', 'Erreur');
        throw err;
      })
    );
  }

  getTodayReservations(hairdresserId: number, date?: string): Observable<IUserReservation[]> {
    return this.#apiService.getTodayReservations(hairdresserId, date).pipe(
      catchError(err => {
        console.error('Error loading today reservations:', err);
        this.#toastrService.error('Erreur lors du chargement des réservations du jour', 'Erreur');
        throw err;
      })
    );
  }

  acceptReservation(reservationId: number): Observable<any> {
    this.#stateService.updateReservationStatusLocally(reservationId, 'accepte');
    this.#stateService.addToUpdatingList(reservationId);

    return this.#apiService.acceptReservation(reservationId).pipe(
      tap(() => {
        this.#stateService.invalidateReservationCache();
        this.#toastrService.success('Réservation acceptée', 'Succès');
      }),
      catchError(err => {
        console.error('Error accepting reservation:', err);
        this.#toastrService.error('Erreur lors de l\'acceptation de la réservation', 'Erreur');
        this.#stateService.rollbackReservationStatus(reservationId);
        throw err;
      }),
      finalize(() => {
        this.#stateService.removeFromUpdatingList(reservationId);
      })
    );
  }

  rejectReservation(reservationId: number): Observable<any> {
    this.#stateService.updateReservationStatusLocally(reservationId, 'refuse');
    this.#stateService.addToUpdatingList(reservationId);

    return this.#apiService.rejectReservation(reservationId).pipe(
      tap(() => {
        this.#stateService.invalidateReservationCache();
        this.#toastrService.success('Réservation rejetée', 'Succès');
      }),
      catchError(err => {
        console.error('Error rejecting reservation:', err);
        this.#toastrService.error('Erreur lors du rejet de la réservation', 'Erreur');
        this.#stateService.rollbackReservationStatus(reservationId);
        throw err;
      }),
      finalize(() => {
        this.#stateService.removeFromUpdatingList(reservationId);
      })
    );
  }

  updateAppointment(updateData: IUpdateAppointmentRequest): Observable<any> {
    return this.#apiService.updateAppointment(updateData).pipe(
      tap(() => {
        this.#stateService.invalidateReservationCache();
        this.#toastrService.success('Nouveau créneau proposé au client', 'Succès');
      }),
      catchError(err => {
        console.error('Error updating appointment:', err);
        this.#toastrService.error('Erreur lors de la proposition de nouveau créneau', 'Erreur');
        throw err;
      })
    );
  }

  valideUpdateAppointment(updateData: any): Observable<any> {
    return this.#apiService.valideUpdateAppointment(updateData).pipe(
      tap(() => {
        this.#stateService.invalidateReservationCache();
        this.#toastrService.success('Nouveau créneau proposé au client', 'Succès');
      }),
      catchError(err => {
        console.error('Error updating appointment:', err);
        this.#toastrService.error('Erreur lors de la proposition de nouveau créneau', 'Erreur');
        throw err;
      })
    );
  }

  cancelReservation(reservationId: number): Observable<any> {
    this.#stateService.updateReservationStatusLocally(reservationId, 'annule');
    this.#stateService.addToUpdatingList(reservationId);

    return this.#apiService.cancelReservation(reservationId).pipe(
      tap(() => {
        this.#stateService.invalidateReservationCache();
        this.#toastrService.success('Réservation annulée', 'Succès');
      }),
      catchError(err => {
        console.error('Error canceling reservation:', err);
        this.#toastrService.error('Erreur lors de l\'annulation de la réservation', 'Erreur');
        this.#stateService.rollbackReservationStatus(reservationId);
        throw err;
      }),
      finalize(() => {
        this.#stateService.removeFromUpdatingList(reservationId);
      })
    );
  }

  finishReservation(reservationId: number): Observable<any> {
    this.#stateService.updateReservationStatusLocally(reservationId, 'termine');
    this.#stateService.addToUpdatingList(reservationId);

    return this.#apiService.finishReservation(reservationId).pipe(
      tap(() => {
        this.#toastrService.success('Réservation terminée', 'Succès');
      }),
      catchError(err => {
        console.error('Error finishing reservation:', err);
        this.#toastrService.error('Erreur lors de la finalisation de la réservation', 'Erreur');
        this.#stateService.rollbackReservationStatus(reservationId);
        throw err;
      }),
      finalize(() => {
        this.#stateService.removeFromUpdatingList(reservationId);
      })
    );
  }

  getFilteredDisponibilities(period: 'week' | 'month' | 'all'): IDispoResponse[] {
    return this.#stateService.getFilteredDisponibilities(period);
  }

  isDisponibilityFree(dispo: IDispoResponse): boolean {
    return this.#stateService.isDisponibilityFree(dispo);
  }

  getOccupancyPercentage(dispo: IDispoResponse): number {
    return this.#stateService.getOccupancyPercentage(dispo);
  }

  getMinutesBetween(startTime: string, endTime: string): number {
    return this.#stateService.getMinutesBetween(startTime, endTime);
  }

  parseCanadianAddress(fullAddress: string) {
    return this.#addressParser.parseCanadianAddress(fullAddress);
  }

  formatFullAddress(address: any): string {
    return this.#addressParser.formatFullAddress(address);
  }

  formatShortAddress(address: any): string {
    return this.#addressParser.formatShortAddress(address);
  }

  getFallbackAddress(fullAddress: string): string {
    return this.#addressParser.getFallbackAddress(fullAddress);
  }

  isWithinFourHours(appointmentDateTime: Date): boolean {
    return this.#validatorService.isWithinFourHours(appointmentDateTime);
  }

  getFormattedAddress(
    fullAddress: string,
    appointmentDate: string,
    appointmentTime: string
  ): string {
    return this.#validatorService.getFormattedAddress(
      fullAddress,
      appointmentDate,
      appointmentTime,
      this.#addressParser
    );
  }

  getTimeUntilFullAddressReveal(appointmentDate: string, appointmentTime: string): string {
    return this.#validatorService.getTimeUntilFullAddressReveal(appointmentDate, appointmentTime);
  }

  updateReservationStatusLocally(reservationId: number, newStatus: "attente" | "attente-confirmation" | "accepte" | "updated" | "refuse" | "annule" | "termine"): void {
    this.#stateService.updateReservationStatusLocally(reservationId, newStatus);
  }

  invalidateReservationCache(): void {
    this.#stateService.invalidateReservationCache();
  }

  rollbackReservationStatus(reservationId: number): void {
    this.#stateService.rollbackReservationStatus(reservationId);
  }

  addToUpdatingList(reservationId: number): void {
    this.#stateService.addToUpdatingList(reservationId);
  }

  removeFromUpdatingList(reservationId: number): void {
    this.#stateService.removeFromUpdatingList(reservationId);
  }

  isReservationUpdating(reservationId: number): boolean {
    return this.#stateService.isReservationUpdating(reservationId);
  }

  checkAndAutoFinalizeAll(): Observable<any[]> {
    return this.getHairdresserReservations().pipe(
      switchMap((reservations: IApiReservation[]) => {
        const acceptedReservations = reservations.filter(r => r.state === 'accepte');
        const autoFinalizeObservables: Observable<any>[] = [];

        acceptedReservations.forEach(reservation => {
          const reservationDateTime = new Date(`${reservation.date_rdv}T${reservation.h_rdv}:00`);
          const now = new Date();
          const hoursSinceAppointment = (now.getTime() - reservationDateTime.getTime()) / (1000 * 60 * 60);

          if (hoursSinceAppointment >= 24) {
            autoFinalizeObservables.push(this.finishReservation(reservation.id));
          }
        });

        if (autoFinalizeObservables.length > 0) {
          return forkJoin(autoFinalizeObservables);
        } else {
          return of([]);
        }
      })
    );
  }
}
